// 스타일 갤러리 데이터 (STYLE_GALLERY.md).
// styleStats 는 공개 읽기 — 랭킹(빌트인+커스텀 통합)과 최근 커스텀 티커를
// 가져온다. 스타일 선택 화면이 열릴 때마다 부르므로 5분 메모리 캐시.

import { db } from '../firebase.js';
import { collection, query, orderBy, limit, getDocs, doc, getDoc } from 'firebase/firestore';

let cache = { at: 0, data: null };
const TTL = 5 * 60 * 1000;

// 랭킹 순서이자 화면의 "디자인 N개" — 최근 30일 그 스타일로 만든 디자인 수 (monthlyUses, 롤업이
// 매시간). 고유 사용자로 셌더니 30일 880장이 수십 명으로 보였다 (2026-10-23). 순서와 숫자는
// 같은 값이어야 한다 — 따로 두었더니 "4위 74명, 5위 101명" 이 됐다. 롤업 전 문서는 누적 uses.
// 2.3.1 이하 앱은 weeklyUsers(7일 고유 사용자)를 읽는다 — 그 앱 문구가 "지난 7일 · N명" 이다.
export function rankUses(s) {
  return typeof s.monthlyUses === 'number' ? s.monthlyUses : Math.max(s.uses || 0, 1);
}

// "커스텀 스타일" 집계 항목 — 롤업 문서가 있으면 카테고리별 커스텀 디자인 수, 없으면 프롬프트별 합.
export function customAggregate(stats, cat, customsCat) {
  return stats?.customTotals?.[cat]?.monthlyUses ?? customsCat.reduce((n, s) => n + rankUses(s), 0);
}

// 커스텀 프롬프트 번역 — 서버가 문서에 캐시하므로 반복 호출 부담 없음.
const translateCache = new Map(); // `${id}_${lang}` → text
export async function translateStyles(ids, target) {
  const missing = ids.filter((id) => !translateCache.has(`${id}_${target}`));
  if (missing.length > 0) {
    try {
      const res = await fetch('https://us-central1-voda-7647c.cloudfunctions.net/translateStyles', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ data: { ids: missing.slice(0, 40), target } }),
      });
      const json = await res.json();
      Object.entries(json.translations || {}).forEach(([id, tr]) => {
        translateCache.set(`${id}_${target}`, tr);
      });
    } catch (e) {
      console.warn('translateStyles failed:', e?.message);
    }
  }
  const out = {};
  ids.forEach((id) => {
    const tr = translateCache.get(`${id}_${target}`);
    if (tr) out[id] = tr;
  });
  return out;
}

export async function fetchStyleStats() {
  if (cache.data && Date.now() - cache.at < TTL) return cache.data;
  try {
    const col = collection(db, 'styleStats');
    const [topSnap, recentSnap, totalsSnap] = await Promise.all([
      getDocs(query(col, orderBy('uses', 'desc'), limit(50))),
      // 100개 = "둘러보기" 랜덤 샘플 풀 — 최신만이 아니라 최근 전반에서
      // 공평하게 노출 기회를 준다 (탐색이 낚아채기 타이밍에 안 걸리게).
      getDocs(query(col, orderBy('lastUsedAt', 'desc'), limit(100))),
      // "커스텀 스타일" 카드 — 롤업이 카테고리별 커스텀 디자인 수를 써 둔다.
      getDoc(doc(col, '_customTotals')).catch(() => null),
    ]);
    const pick = (d) => ({ id: d.id, ...d.data() });
    const visible = (s) => s.type === 'builtin' || (s.public !== false && s.prompt);

    // 순서 = 최근 30일 사용 횟수, 같으면 누적 횟수.
    const byUses = (a, b) => rankUses(b) - rankUses(a)
      || (b.uses || 0) - (a.uses || 0)
      || (b.lastUsedAt?.toMillis?.() || 0) - (a.lastUsedAt?.toMillis?.() || 0);

    const all = topSnap.docs.map(pick).filter(visible);
    const recent = recentSnap.docs.map(pick).filter((s) => s.type === 'custom' && visible(s));
    const builtins = all.filter((s) => s.type === 'builtin').sort(byUses);
    // customs = 인기(30일 사용 횟수)순 + 최근 사용분 병합 (dedup)
    const seen = new Set();
    const customs = [...all.filter((s) => s.type === 'custom'), ...recent]
      .filter((s) => (seen.has(s.id) ? false : seen.add(s.id)))
      .sort(byUses);

    const data = { builtins, customs, customTotals: totalsSnap?.data()?.byCategory || {} };
    cache = { at: Date.now(), data };
    return data;
  } catch (e) {
    console.warn('fetchStyleStats failed:', e?.message);
    return { builtins: [], customs: [], customTotals: {} };
  }
}
